"use client";

import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { WebSocketMessage } from "../../types/WebSocketMessage";
import { getWsConfig } from "./apiConfig";
import { signalRTokenApi } from "./signalRToken.api";
import { errorLogger } from "../services/errorLogger.service";
import { retrySignalR } from "../config/retryConfig";

const isBrowser = typeof window !== "undefined";

// DTO for SignalR messages (compatible with DataSyncMessage from backend)
export interface SignalRMessage {
  location?: string;
  channel?: string;
  action: string;
  method?: string | null;
  fields?: Array<{
    fieldName: string;
    value: unknown;
    rowId?: string;
  }> | null;
  rows?: string[] | null;
  success: boolean | null;
  error?: string | null;
  entityId?: string | null;
  permission?: boolean;
}

class SignalRManager {
  private static isInitializing = false;
  private connection: HubConnection | null = null;
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null;
  private subscriptions = new Set<string>();
  private locationFilters = new Set<string>();
  private isGlobalChannelSubscribed = false;
  private connectionCallbacks: {
    onMessage: (message: string) => void;
    onError: (error: string) => void;
    onClose: (message: string) => void;
    onOpen?: () => void;
  } | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  /**
   * Connect to SignalR hub
   */
  async connect(
    _token: string,
    message: WebSocketMessage,
    onMessage: (message: string) => void,
    onError: (error: string) => void,
    onClose: (message: string) => void,
    onOpen?: () => void
  ): Promise<void> {
    if (!isBrowser) {
      console.log("SignalR: Not in browser environment, skipping connection");
      return;
    }

    // Already connected
    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      console.log("SignalR already connected.");
      onOpen?.();
      return;
    }

    // Wait if initializing
    if (SignalRManager.isInitializing) {
      console.log("SignalR already initializing, waiting...");
      let waitRetries = 0;
      while (waitRetries < 40 && SignalRManager.isInitializing) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        waitRetries++;

        if (this.connection && this.connection.state === HubConnectionState.Connected) {
          console.log("SignalR connected while waiting");
          onOpen?.();
          return;
        }
      }

      if (this.connection && this.connection.state === HubConnectionState.Connected) {
        onOpen?.();
        return;
      }
    }

    // Wait if connecting
    if (this.connection && this.connection.state === HubConnectionState.Connecting) {
      console.log("SignalR already connecting, waiting...");
      let waitRetries = 0;
      while (waitRetries < 20) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        waitRetries++;

        if (!this.connection) break;

        const currentState = this.connection.state as HubConnectionState;
        if (currentState === HubConnectionState.Connected) {
          console.log("SignalR connected after waiting");
          onOpen?.();
          return;
        } else if (
          currentState === HubConnectionState.Disconnected ||
          currentState === HubConnectionState.Disconnecting
        ) {
          break;
        }
      }
    }

    SignalRManager.isInitializing = true;
    this.connectionCallbacks = { onMessage, onError, onClose, onOpen };

    try {
      await retrySignalR(
        async () => await this.createConnection(message),
        {
          onRetry: (err, attempt, delay) => {
            console.log(`Retrying SignalR connection (${attempt}) after ${delay}ms`);
            errorLogger.logSignalRError(
              `SignalR connection retry attempt ${attempt}`,
              err instanceof Error ? err : undefined,
              { attempt, delay }
            );
          },
        }
      );
      SignalRManager.isInitializing = false;
    } catch (error) {
      SignalRManager.isInitializing = false;
      console.error("SignalR connection failed:", error);
      errorLogger.logSignalRError(
        "SignalR connection failed after retries",
        error instanceof Error ? error : undefined
      );
      onError(error instanceof Error ? error.message : "Connection failed");
    }
  }

  /**
   * Create new SignalR connection with compact token
   */
  private async createConnection(message: WebSocketMessage): Promise<void> {
    if (!this.connectionCallbacks) {
      throw new Error("Connection callbacks not set");
    }

    const { onMessage, onError, onClose, onOpen } = this.connectionCallbacks;
    const wsConfig = getWsConfig();

    try {
      // Exchange JWT for compact SignalR token
      console.log("Exchanging JWT for compact SignalR token...");
      const tokenResponse = await signalRTokenApi.exchangeForSignalRToken();
      const compactToken = tokenResponse.signalRToken;

      this.connection = new HubConnectionBuilder()
        .withUrl(`${wsConfig.url}?access_token=${compactToken}`, {
          withCredentials: true,
          timeout: 300000,
          skipNegotiation: false,
          transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents,
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => null,
        })
        .build();

      if (!this.connection) {
        throw new Error("Failed to create SignalR connection");
      }

      // Configure timeouts
      (this.connection as unknown as { serverTimeoutInMilliseconds: number }).serverTimeoutInMilliseconds = 300000;
      (this.connection as unknown as { keepAliveIntervalInMilliseconds: number }).keepAliveIntervalInMilliseconds = 30000;

      // Setup message handler
      this.connection.on("ReceiveDataUpdate", (signalRMessage: SignalRMessage) => {
        console.log("SignalR message received:", signalRMessage);

        const messageLocation = signalRMessage.location || signalRMessage.channel || "";

        if (!messageLocation) {
          console.warn("Message received without location or channel:", signalRMessage);
          return;
        }

        if (!this.shouldProcessMessage(messageLocation)) {
          console.log(`Message filtered out for location: ${messageLocation}`);
          return;
        }

        // Convert SignalRMessage to WebSocketMessage format
        const webSocketMessage: WebSocketMessage = {
          location: messageLocation,
          channel: messageLocation,
          action: signalRMessage.action,
          method: signalRMessage.method || null,
          fields:
            signalRMessage.fields?.map((f) => ({
              fieldName: f.fieldName,
              value: String(f.value),
              rowId: f.rowId ? parseInt(f.rowId) : 0,
            })) || null,
          rows: signalRMessage.rows || null,
          success: signalRMessage.success,
          error: signalRMessage.error || null,
          permission: signalRMessage.permission,
          entityId: signalRMessage.entityId
            ? parseInt(signalRMessage.entityId)
            : null,
        };

        onMessage(JSON.stringify(webSocketMessage));
      });

      // Channel subscription confirmation
      this.connection.on("ChannelSubscribed", (channel: string) => {
        console.log(`SignalR confirmed subscription to channel: ${channel}`);
      });

      // Error handler
      this.connection.on("ErrorOccurred", (error: string) => {
        console.error(`SignalR error occurred: ${error}`);
        errorLogger.logSignalRError("SignalR error occurred", undefined, {
          additionalData: { error },
        });
        onError(error);
      });

      // Connection close handler
      this.connection.onclose(async (error: Error | undefined) => {
        console.log("SignalR connection closed:", error);
        errorLogger.logSignalRError(
          "SignalR connection closed",
          error,
          { reconnectAttempts: this.reconnectAttempts }
        );
        this.connection = null;
        this.clearHeartbeat();

        // Auto-reconnect
        if (this.connectionCallbacks && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(
            `Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts} with fresh token...`
          );

          const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts - 1), 30000);
          setTimeout(async () => {
            try {
              await this.createConnection(message);
              this.reconnectAttempts = 0;
              await this.resubscribeToChannels();
              console.log("Reconnection successful with fresh token");
            } catch (reconnectError) {
              console.error("Reconnection with fresh token failed:", reconnectError);
              if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                onClose(error?.message || "Connection closed - max attempts reached");
              }
            }
          }, delay);
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          onClose(error?.message || "Connection closed - max attempts reached");
        } else {
          onClose(error?.message || "Connection closed");
        }
      });

      // Start connection
      await this.connection.start();

      // Wait for connection to be established
      let retries = 0;
      const maxRetries = 50;

      while (this.connection.state !== HubConnectionState.Connected && retries < maxRetries) {
        console.log(
          `Waiting for SignalR connection... State: ${this.connection.state} (attempt ${retries + 1}/${maxRetries})`
        );
        await new Promise((resolve) => setTimeout(resolve, 100));
        retries++;
      }

      if (this.connection.state === HubConnectionState.Connected) {
        console.log("SignalR connected successfully");
      } else if (this.connection.state === HubConnectionState.Connecting) {
        console.warn(`SignalR still connecting after ${retries} retries - continuing anyway...`);
      } else {
        throw new Error(
          `Connection failed with state: ${this.connection.state} after ${retries} retries`
        );
      }

      // Setup heartbeat
      this.setupHeartbeat();

      // Subscribe to global channel
      await this.subscribeToGlobalChannel();

      onOpen?.();
    } catch (error) {
      console.error("SignalR connection creation failed:", error);
      throw error;
    }
  }

  /**
   * Subscribe to global channel
   */
  private async subscribeToGlobalChannel(): Promise<void> {
    if (!this.connection) {
      console.error("SignalR not connected. Cannot subscribe to global channel.");
      return;
    }

    if (this.connection.state !== HubConnectionState.Connected) {
      console.warn(
        `SignalR not in Connected state (${this.connection.state}). Cannot subscribe to global channel.`
      );
      return;
    }

    try {
      if (!this.isGlobalChannelSubscribed) {
        console.log("Subscribing to GlobalDataSync channel...");

        await Promise.race([
          this.connection.invoke("SubscribeToChannel", "GlobalDataSync"),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Subscription timeout")), 10000)
          ),
        ]);

        console.log("SignalR subscribed to channel: GlobalDataSync");
        this.isGlobalChannelSubscribed = true;
      }
    } catch (error) {
      console.error("Failed to subscribe to global SignalR channel:", error);

      if (this.connection.state === HubConnectionState.Connected) {
        console.log("Retrying subscription in 2 seconds...");
        setTimeout(async () => {
          try {
            if (
              this.connection?.state === HubConnectionState.Connected &&
              !this.isGlobalChannelSubscribed
            ) {
              await this.connection.invoke("SubscribeToChannel", "GlobalDataSync");
              console.log("SignalR subscribed to channel: GlobalDataSync (retry successful)");
              this.isGlobalChannelSubscribed = true;
            }
          } catch (retryError) {
            console.error("Retry subscription also failed:", retryError);
          }
        }, 2000);
      }
    }
  }

  /**
   * Add location filter
   */
  addLocationFilter(location: string): void {
    this.locationFilters.add(location);
    console.log(`Added location filter: ${location}`);
  }

  /**
   * Remove location filter
   */
  removeLocationFilter(location: string): void {
    this.locationFilters.delete(location);
    console.log(`Removed location filter: ${location}`);
  }

  /**
   * Check if message should be processed based on location filters
   */
  private shouldProcessMessage(location: string): boolean {
    if (!location || location.trim() === "") {
      return false;
    }

    if (this.locationFilters.size === 0) {
      return true;
    }

    return this.locationFilters.has(location);
  }

  /**
   * Send message through SignalR
   */
  async sendMessage(message: WebSocketMessage): Promise<void> {
    let attempts = 0;
    const maxAttempts = 10;

    while (
      (!this.connection || this.connection.state !== HubConnectionState.Connected) &&
      attempts < maxAttempts
    ) {
      console.log(`Waiting for SignalR connection... attempt ${attempts + 1}/${maxAttempts}`);
      await new Promise((resolve) => setTimeout(resolve, 500));
      attempts++;
    }

    if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
      console.warn("SignalR connection not available after waiting, message will be dropped");
      return;
    }

    try {
      const locationValue = message.location || message.channel || "";

      const signalRMessage: SignalRMessage = {
        location: locationValue,
        action: message.action,
        method: message.method,
        fields:
          message.fields?.map((field) => ({
            fieldName: field.fieldName,
            value: field.value,
            rowId: field.rowId?.toString(),
          })) || null,
        rows: message.rows,
        success: message.success,
        error: message.error,
        entityId: message.entityId?.toString() || null,
        permission: message.permission,
      };

      console.log("Sending SignalR message:", {
        location: locationValue,
        action: message.action,
        method: message.method,
      });

      try {
        await this.connection.invoke("BroadcastToChannel", "GlobalDataSync", signalRMessage);
        console.log(`Message sent via BroadcastToChannel with location: ${signalRMessage.location}`);
      } catch {
        console.log("BroadcastToChannel failed, trying NotifyDataUpdate...");

        try {
          await this.connection.invoke("NotifyDataUpdate", signalRMessage);
          console.log("Message sent via NotifyDataUpdate");
        } catch {
          console.warn("All SignalR send methods failed");
        }
      }
    } catch (error) {
      console.error("Failed to process SignalR message:", error);
      throw error;
    }
  }

  /**
   * Resubscribe to channels after reconnection
   */
  private async resubscribeToChannels(): Promise<void> {
    console.log("Resubscribing to global channel...");

    let attempts = 0;
    const maxAttempts = 10;

    while (this.connection?.state !== HubConnectionState.Connected && attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      attempts++;
    }

    if (this.connection?.state !== HubConnectionState.Connected) {
      console.error("Connection not ready for resubscription after waiting");
      return;
    }

    this.isGlobalChannelSubscribed = false;
    await this.subscribeToGlobalChannel();

    const filters = Array.from(this.locationFilters);
    this.locationFilters.clear();
    filters.forEach((filter) => this.addLocationFilter(filter));

    console.log(`Resubscribed to global channel with ${filters.length} location filters`);
  }

  /**
   * Subscribe to channel (legacy interface)
   */
  async subscribe(channel: string): Promise<void> {
    console.log(`Subscribing to SignalR channel: ${channel}`);
    this.addLocationFilter(channel);
    await this.subscribeToGlobalChannel();
  }

  /**
   * Unsubscribe from channel (legacy interface)
   */
  async unsubscribe(channel: string): Promise<void> {
    console.log(`Unsubscribed from SignalR channel: ${channel}`);
    this.removeLocationFilter(channel);
  }

  /**
   * Setup heartbeat for connection monitoring
   */
  private setupHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    this.heartbeatInterval = setInterval(() => {
      if (this.connection?.state === HubConnectionState.Connected) {
        console.log("SignalR connection active - keep-alive working");
      } else {
        console.warn(`SignalR connection state: ${this.connection?.state || "null"}`);
      }
    }, 30000);
  }

  /**
   * Clear heartbeat timer
   */
  private clearHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Disconnect SignalR
   */
  async disconnect(): Promise<void> {
    try {
      this.clearHeartbeat();

      if (this.connection && this.connection.state !== HubConnectionState.Disconnected) {
        console.log("Disconnecting SignalR...");
        await this.connection.stop();
      }

      this.connection = null;
      this.isGlobalChannelSubscribed = false;
      this.locationFilters.clear();
      this.subscriptions.clear();
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error("Error during SignalR disconnect:", error);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  /**
   * Get subscribed channels
   */
  getSubscribedChannels(): string[] {
    return Array.from(this.locationFilters);
  }

  /**
   * Get connection state
   */
  getConnectionState(): string {
    if (!this.connection) return "Disconnected";

    switch (this.connection.state) {
      case HubConnectionState.Connected:
        return "Connected";
      case HubConnectionState.Connecting:
        return "Connecting";
      case HubConnectionState.Reconnecting:
        return "Reconnecting";
      case HubConnectionState.Disconnected:
        return "Disconnected";
      case HubConnectionState.Disconnecting:
        return "Disconnecting";
      default:
        return "Unknown";
    }
  }
}

// Singleton instance
export const signalRManager = new SignalRManager();

// Legacy compatibility alias
export const webSocketManager = signalRManager;
